import { toast, ToastOptions, Toast } from 'react-hot-toast';
import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { ToastMessageTemplate } from './toast-message-template';

const successIcon = getDesignSystemIcon('icon-checkbox-inverted.svg');
const warningIcon = getDesignSystemIcon('icon-triangle-exclamationmark-16.svg');
const errorIcon = getDesignSystemIcon('icon-error-inverted.svg');
const infoIcon = getDesignSystemIcon('icon-circle-info.svg');

type PhoenixToastOptions = ToastOptions & {
  closable?: boolean;
};

const defaultOptions: PhoenixToastOptions = {
  position: 'top-center',
  duration: 5000,
  closable: false,
  removeDelay: 200,
};

export const useToast = () => {
  const getAriaProps = (role: Toast['ariaProps']['role']): Toast['ariaProps'] => ({
    role,
    'aria-live': 'polite',
  });

  const showSuccessNotification = (message: string, options?: PhoenixToastOptions) => {
    const _options = {
      id: 'successNotification',
      ariaProps: getAriaProps('status'),
      ...defaultOptions,
      ...options,
    };

    toast.custom(
      (t) => (
        <ToastMessageTemplate
          backgroundColorClass="bg-green-light"
          iconColorClass="text-green-deep"
          icon={successIcon}
          message={message}
          id={t.id}
          closable={_options.closable}
          dataTestId="successNotification"
        />
      ),
      _options
    );
  };

  const showWarningNotification = (message: string, options?: PhoenixToastOptions) => {
    const _options = {
      id: 'warningNotification',
      ariaProps: getAriaProps('alert'),
      ...defaultOptions,
      ...options,
    };

    toast.custom(
      (t) => (
        <ToastMessageTemplate
          backgroundColorClass="bg-yellow-light"
          iconColorClass="text-yellow-deep"
          icon={warningIcon}
          message={message}
          id={t.id}
          closable={_options.closable}
          dataTestId="warningNotification"
        />
      ),
      _options
    );
  };

  const showErrorNotification = (message: string, options?: PhoenixToastOptions) => {
    const _options = {
      id: 'errorNotification',
      ariaProps: getAriaProps('alert'),
      ...defaultOptions,
      ...options,
    };

    toast.custom(
      (t) => (
        <ToastMessageTemplate
          backgroundColorClass="bg-red-light"
          iconColorClass="text-red-deep"
          icon={errorIcon}
          message={message}
          id={t.id}
          closable={_options.closable}
          dataTestId="errorNotification"
        />
      ),
      _options
    );
  };

  const showInfoNotification = (message: string, options?: PhoenixToastOptions) => {
    const _options = {
      id: 'infoNotification',
      ariaProps: getAriaProps('status'),
      ...defaultOptions,
      ...options,
    };

    toast.custom(
      (t) => (
        <ToastMessageTemplate
          backgroundColorClass="bg-secondary-light"
          iconColorClass="text-secondary"
          icon={infoIcon}
          message={message}
          id={t.id}
          closable={_options.closable}
          dataTestId="infoNotification"
        />
      ),
      _options
    );
  };

  return {
    showSuccessNotification,
    showWarningNotification,
    showErrorNotification,
    showInfoNotification,
  };
};
